import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { ColorKeys } from '@/styles/colors';
import beTwMerge from '@/utils/beTwMerge';
import useMediaQuery from '@/hooks/useMediaQuery';
import Button from '@/components/homepage/Button/Button';
import DropdownButton from '@/components/homepage/DropdownButton/DropdownButton';
import { ResponsiveImageSet } from '@/utils/ResponsiveImage';
import { useBuilderProvider } from '@/context/BuilderContext';
import { isMobileDevice } from '@/utils/utils';
import RichText from '../RichText/RichText';
import { VideoPlayerHandle } from './HeroVideoPlayer';

const WistiaContainerDynamic = dynamic(() => import('../WistiaContainer/WistiaContainer'));

const VideoPlayerDynamic = dynamic(() => import('./HeroVideoPlayer'));

const baseDropdownButtonStyle = `
  tw-basis-[calc(50%-8px)] tw-max-w-none [&>button]:tw-w-full [&>button]:tw-h-full [&>button]:tw-leading-4 [&>button]:tw-py-3 [&>button>span]:tw-max-w-[calc(100%-28px)] [&>button>span]:tw-overflow-hidden tw-h-[44px] tw-whitespace-nowrap [&>button>span]:tw-text-ellipsis
`;

const baseButtonStyle = `
  tw-basis-[calc(50%-8px)] tw-max-w-none tw-whitespace-nowrap tw-text-ellipsis tw-overflow-hidden
`;

export type ResponsiveSet = {
  desktop?: string;
  tablet?: string;
  mobile?: string;
};

export type Action = {
  label: string;
  variant?: 'primary' | 'secondary';
  color?: ColorKeys;
  mobileVariant?: 'primary' | 'secondary';
  mobileColor?: ColorKeys;
  onClick?: () => void;
  href?: string;
  target?: string;
  actions?: Action[];
};

export type HeroContentVerticalPosition = 'top' | 'center' | 'bottom' | '';
export type HeroContentHorizontalPosition = 'left' | 'center' | 'center/center' | 'right' | '';
export type HeroVariant = 'large' | 'standard';

export type HeroBannerProps = {
  title?: string;
  titleTag?: 'h1' | 'h2' | 'h3';
  description?: string;
  actions?: Action[];
  background: ResponsiveImageSet;
  backgroundMobile?: ResponsiveImageSet;
  contentVerticalPosition?: HeroContentVerticalPosition;
  contentVerticalPositionTablet?: HeroContentVerticalPosition;
  contentVerticalPositionDesktop?: HeroContentVerticalPosition;
  contentHorizontalPosition?: HeroContentHorizontalPosition;
  contentHorizontalPositionTablet?: HeroContentHorizontalPosition;
  contentHorizontalPositionDesktop?: HeroContentHorizontalPosition;
  contentColor?: string;
  contentColorTablet?: string;
  contentColorDesktop?: string;
  type?: HeroVariant;
  videoSrc?: string;
  mobileVideoSrc?: string;
  fileVideoSrc?: string;
  fileMobileVideoSrc?: string;
  loop?: boolean;
  controls?: boolean;
  autoPlay?: boolean;
  sectionLink?: string;
  imgAlt?: string;
  className?: string;
  hasSound?: boolean;
  mobileTitle?: string;
};

export default function HeroBanner({
  title,
  titleTag = 'h1',
  description,
  background,
  backgroundMobile,
  actions,
  contentVerticalPosition = 'center',
  contentVerticalPositionTablet = '',
  contentVerticalPositionDesktop = '',
  contentHorizontalPosition = 'left',
  contentHorizontalPositionTablet = '',
  contentHorizontalPositionDesktop = 'left',
  contentColor = 'light',
  contentColorTablet = '',
  contentColorDesktop = '',
  type = 'large',
  videoSrc,
  mobileVideoSrc,
  loop = true,
  controls = true,
  autoPlay = true,
  fileVideoSrc,
  fileMobileVideoSrc,
  sectionLink,
  imgAlt,
  className,
  hasSound = true,
  mobileTitle,
}: HeroBannerProps) {
  const { device } = useBuilderProvider();
  const isDesktop = useMediaQuery('(min-width: 1024px)', device === 'desktop');
  const [ready, setReady] = useState(false);
  const TitleTag = titleTag;
  const videoPlayerRef = useRef<VideoPlayerHandle>(null);

  const handleMouseEnter = () => {
    if (videoPlayerRef.current) {
      videoPlayerRef.current.setShowControls(true);
    }
  };

  const handleMouseLeave = () => {
    if (videoPlayerRef.current) {
      videoPlayerRef.current.setShowControls(false);
    }
  };

  const videoOptions = {
    autoplay: autoPlay,
    controls,
    loop,
    muted: true,
    hasSound,
    variant: type,
    position: contentHorizontalPositionDesktop,
  };

  const mobileBackground = Array.isArray(backgroundMobile) ? backgroundMobile[0]?.src : undefined;
  const desktopBackground = Array.isArray(background)
    ? background.filter(i => i.breakpoint === 1024)[0].src
    : undefined;

  const backgroundImage = isDesktop ? desktopBackground : mobileBackground;

  // eslint-disable-next-line no-nested-ternary
  const calculatedVideoSrc = ready ? (isDesktop ? fileVideoSrc : fileMobileVideoSrc) : fileVideoSrc;

  const positionClassNames = `${
    {
      top: 'tw-items-start',
      center: 'tw-items-center',
      bottom: 'tw-items-end',
    }[contentVerticalPosition]
  } ${
    {
      top: 'md:tw-items-start',
      center: 'md:tw-items-center',
      bottom: 'md:tw-items-end',
    }[contentVerticalPositionTablet]
  } ${
    {
      top: 'lg:tw-items-start',
      center: contentHorizontalPositionDesktop === 'center' ? 'lg:tw-items-end' : 'lg:tw-items-center',
      bottom: 'lg:tw-items-end',
    }[contentVerticalPositionDesktop]
  } ${
    {
      left: 'tw-justify-start',
      center: 'tw-justify-center',
      right: 'tw-justify-end',
    }[contentHorizontalPosition]
  } ${
    {
      left: 'md:tw-justify-start',
      center: 'md:tw-justify-center',
      right: 'md:tw-justify-end',
    }[contentHorizontalPositionTablet]
  } ${
    {
      left: 'lg:tw-justify-start',
      center: 'lg:tw-justify-center',
      right: 'lg:tw-justify-end',
    }[contentHorizontalPositionDesktop]
  }`;

  const contentClassNames = `${
    {
      left: 'tw-text-left',
      center: 'tw-text-center',
      right: 'tw-text-left',
    }[contentHorizontalPosition]
  } ${
    {
      left: 'md:tw-text-left',
      center: 'md:tw-text-center',
      right: 'md:tw-text-left',
    }[contentHorizontalPositionTablet]
  } ${
    {
      left: 'lg:tw-text-left',
      center: 'lg:tw-text-center',
      right: 'lg:tw-text-left',
    }[contentHorizontalPositionDesktop]
  } ${
    {
      light: 'tw-text-color-white',
      dark: 'tw-text-color-black',
    }[contentColor]
  } ${
    {
      light: 'md:tw-text-color-white',
      dark: 'md:tw-text-color-black',
    }[contentColorTablet]
  } ${
    {
      light: 'lg:tw-text-color-white',
      dark: 'lg:tw-text-color-black',
    }[contentColorDesktop]
  }`;

  const variantOptions = {
    large: {
      imageHeight: '520',
      mainWrapper: 'tw-aspect-[1/1.33] lg:tw-aspect-[36/13]',
      imageWrapper: '',
      contentWrapper: 'tw-px-[16px] tw-pb-[24px] md:tw-p-[56px] lg:tw-p-[64px]',
      content: '',
      desktopButton: '',
      mobileButton: '',
    },
    standard: {
      imageHeight: '450',
      mainWrapper: 'tw-aspect-auto lg:tw-aspect-[16/5] tw-flex-col',
      imageWrapper: 'max-[1023px]:tw-aspect-[6/5] max-[1023px]:tw-block',
      contentWrapper: `tw-relative tw-inset-x-auto tw-inset-y-auto tw-px-4 tw-pb-0 ${mobileTitle ? 'tw-pt-[40px]' : 'tw-pt-[24px]'} md:tw-px-0 lg:tw-p-[64px] max-[1023px]:tw-bg-white lg:tw-absolute lg:tw-top-0 lg:tw-left-0 lg:tw-bottom-0 lg:tw-right-0`,
      content: 'max-[1023px]:tw-text-color-black',
      desktopButton: '!tw-hidden lg:!tw-block',
      mobileButton: '!tw-block lg:!tw-hidden',
    },
  };

  const getMedia = () => {
    const images = (
      <>
        {desktopBackground && (
          <Image
            src={desktopBackground}
            fill
            priority={isDesktop}
            quality={95}
            alt={imgAlt || title || ''}
            sizes="(min-width: 1024px) 100vw"
            className="tw-hidden lg:tw-block tw-object-cover"
          />
        )}
        {mobileBackground && (
          <Image
            src={mobileBackground}
            fill
            priority={!isDesktop}
            quality={95}
            alt={imgAlt || title || ''}
            sizes="(max-width: 1024px) 100vw"
            className="tw-block lg:tw-hidden"
          />
        )}
      </>
    );

    if (videoSrc) {
      return (
        <div
          className="tw-relative tw-w-full tw-h-full tw-object-cover tw-bg-cover tw-overflow-hidden"
          aria-label={imgAlt}
        >
          {desktopBackground && (
            <Image
              src={desktopBackground}
              fill
              priority={isDesktop}
              quality={95}
              alt=""
              sizes="(min-width: 1024px) 100vw"
              className="tw-hidden lg:tw-block tw-object-cover"
            />
          )}
          {mobileBackground && (
            <Image
              src={mobileBackground}
              fill
              priority={!isDesktop}
              quality={95}
              alt=""
              sizes="(max-width: 1024px) 100vw"
              className="tw-block lg:tw-hidden"
            />
          )}
          <WistiaContainerDynamic
            wistiaURL={videoSrc}
            className="tw-hidden lg:tw-block tw-w-full tw-h-full tw-border-0 tw-overflow-hidden tw-absolute"
          />
          <WistiaContainerDynamic
            wistiaURL={mobileVideoSrc!}
            className={beTwMerge(
              'tw-block lg:tw-hidden tw-w-full tw-h-full tw-border-0 tw-overflow-hidden tw-absolute'
            )}
          />
          {/* Overlay Div */}
          {sectionLink && (
            <>
              {/* Top Overlay */}
              <a
                href={sectionLink}
                className="tw-block tw-absolute tw-top-0 tw-left-0"
                style={{ height: 'calc(100% - 52px)', width: 'calc(100% - 100px)', cursor: 'pointer' }}
                role="button"
                aria-label="top overlay"
              />
              {/* Right Overlay */}
              <a
                href={sectionLink}
                className="tw-block tw-absolute tw-right-0"
                style={{
                  height: hasSound ? 'calc(100% - 152px)' : 'calc(100% - 52px)',
                  width: '100px',
                  top: hasSound ? '100px' : '0',
                  cursor: 'pointer',
                }}
                aria-label="right overlay"
              />
              {/* Bottom Overlay */}
              <a
                href={sectionLink}
                className="tw-block tw-absolute tw-bottom-0 tw-right-[104px]"
                /* Play, captions, volume, and buttons */
                style={{ height: '52px', width: 'calc(100% - 52px - 52px - 52px)', cursor: 'pointer' }}
                aria-label="bottom overlay"
              />
            </>
          )}
          {/* Bottom Band for Wistia Controls */}
          {sectionLink && (
            <div className="tw-absolute tw-bottom-0 tw-left-0" style={{ height: '52px', width: '52px', zIndex: -1 }} />
          )}
        </div>
      );
    }

    if (fileVideoSrc) {
      return (
        <div className="tw-w-full tw-h-full tw-overflow-hidden" aria-label={imgAlt}>
          <div
            style={{ backgroundImage: `url(${backgroundImage})` }}
            className="tw-absolute tw-w-full tw-h-full tw-object-cover tw-bg-cover tw-overflow-hidden"
          />
          <VideoPlayerDynamic
            ref={videoPlayerRef}
            videoSrc={calculatedVideoSrc}
            poster={isDesktop ? desktopBackground : mobileBackground}
            options={videoOptions}
            className="tw-bg-transparent"
          />
          {/* Overlay Div */}
          {sectionLink && (
            <>
              <a
                href={sectionLink}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={beTwMerge(
                  'tw-absolute tw-w-full tw-left-0',
                  type === 'large' ? 'tw-bottom-0 lg:tw-top-0' : 'tw-top-0'
                )}
                style={{ height: 'calc(100% - 85px)', cursor: 'pointer' }}
                role="button"
                aria-label={imgAlt}
                tabIndex={0}
              />
              <a
                href={sectionLink}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={beTwMerge(
                  'tw-absolute tw-left-0',
                  type === 'large' ? 'tw-top-0 lg:tw-bottom-0' : 'tw-bottom-0'
                )}
                style={{ height: '85px', width: 'calc(100% - 124px)', cursor: 'pointer' }}
                aria-hidden
              />
              <a
                href={sectionLink}
                className={beTwMerge(
                  'tw-absolute tw-right-0',
                  type === 'large' ? 'tw-top-0 lg:tw-bottom-0' : 'tw-bottom-0'
                )}
                style={{ height: '85px', width: '124px', zIndex: -1 }}
              />
            </>
          )}
        </div>
      );
    }

    if (actions?.length) {
      /**
       * Be aware that anchor inside anchor causes hydration errors. Use anchors when necessary only
       */
      return (
        <a
          href={actions?.[0]?.href}
          aria-label={
            actions?.[0]?.label && actions?.[0]?.href ? `${actions?.[0]?.label} of ${actions?.[0]?.href}` : imgAlt
          }
          className="tw-w-full tw-inline-block tw-overflow-hidden tw-h-full tw-relative"
        >
          {images}
        </a>
      );
    }

    return (
      <div aria-label={imgAlt} className="tw-w-full tw-inline-block tw-overflow-hidden tw-h-full tw-relative">
        {images}
      </div>
    );
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setReady(true);
    }
  }, []);

  const selectedTitle = ready && isMobileDevice() ? mobileTitle : title;

  return (
    <div className="tw-w-full">
      <div
        className={beTwMerge(
          `tw-bg-color-grey-100 tw-mx-auto tw-relative min-[1440px]:tw-w-full min-[1440px]:tw-h-full tw-flex`,
          variantOptions[type]['mainWrapper'],
          className
        )}
        role="banner"
        aria-label={selectedTitle || imgAlt || ''}
      >
        <div
          className={beTwMerge(
            'tw-w-full tw-inline-block tw-overflow-hidden tw-h-full tw-relative',
            `${variantOptions[type]['imageWrapper']}`
          )}
          role="banner"
          aria-label={selectedTitle || imgAlt || ''}
        >
          {getMedia()}
        </div>

        <div
          className={beTwMerge(
            `tw-absolute tw-top-0 tw-left-0 tw-bottom-0 tw-right-0 tw-flex tw-pointer-events-none min-[1024px]:max-[1130px]:tw-pr-[44px]`,
            positionClassNames,
            variantOptions[type]['contentWrapper']
          )}
        >
          <div
            className={beTwMerge(
              `tw-max-w-[480px] tw-w-full min-[1024px]:max-[1130px]:tw-w-[420px]`,
              contentClassNames,
              variantOptions[type]['content']
            )}
          >
            {selectedTitle ? (
              <TitleTag dangerouslySetInnerHTML={{ __html: selectedTitle }} className="tw-mb-[16px]" />
            ) : (
              <div className="tw-mb-[16px]" />
            )}
            {description && (
              <div className={beTwMerge(actions && actions.length > 0 && 'tw-mb-[24px]')}>
                <RichText
                  className="tw-font-book tw-pointer-events-auto [&>p>a]:tw-text-inherit"
                  content={description}
                />
              </div>
            )}

            <div
              className={beTwMerge(
                'tw-pointer-events-auto tw-flex tw-items-center tw-justify-center md:tw-flex-row tw-flex-col tw-flex-wrap md:tw-gap-x-[16px] tw-gap-y-2 lg:tw-gap-y-4 tw-gap-x-0',
                contentHorizontalPositionDesktop === 'center' || contentHorizontalPositionDesktop === 'center/center'
                  ? ''
                  : 'lg:tw-justify-between'
              )}
            >
              {actions?.slice(0, 2).map(action => {
                const { label, color, variant, mobileColor, mobileVariant } = action;

                if (action.actions) {
                  const { actions: subActions } = action;
                  return (
                    <>
                      <DropdownButton
                        key={`dropdown-${label}-desktop`}
                        label={label}
                        options={subActions}
                        color={color}
                        type={variant}
                        className={beTwMerge(
                          baseDropdownButtonStyle,
                          '!tw-hidden lg:!tw-block',
                          actions.length > 1 ? 'tw-flex-1' : 'tw-flex-1 lg:tw-flex-[0_0_calc(50%_-_0.5rem)]'
                        )}
                      />

                      <DropdownButton
                        key={`dropdown-${label}-mobile`}
                        label={label}
                        options={subActions}
                        color={mobileColor || color}
                        type={mobileVariant || variant}
                        className={beTwMerge(
                          baseDropdownButtonStyle,
                          '!tw-block lg:!tw-hidden',
                          actions.length > 1 ? 'tw-flex-1' : 'tw-flex-1 lg:tw-flex-[0_0_calc(50%_-_0.5rem)]'
                        )}
                      />
                    </>
                  );
                }
                const { onClick, href, target } = action;
                return (
                  <>
                    <Button
                      className={beTwMerge(
                        baseButtonStyle,
                        '!tw-hidden lg:!tw-block',
                        actions.length > 1
                          ? 'tw-flex-1 md:tw-max-w-[calc(50%-8px)]'
                          : 'tw-flex-1 lg:tw-flex-[0_0_calc(50%_-_0.5rem)]'
                      )}
                      key={`button-${label}-desktop`}
                      label={label}
                      color={color}
                      type={variant}
                      href={href}
                      target={target}
                      onClick={onClick}
                    />

                    <Button
                      className={beTwMerge(
                        baseButtonStyle,
                        '!tw-block lg:!tw-hidden',
                        actions.length > 1
                          ? 'tw-flex-1 md:tw-max-w-[calc(50%-8px)]'
                          : 'tw-flex-1 lg:tw-flex-[0_0_calc(50%_-_0.5rem)]'
                      )}
                      key={`button-${label}-mobile`}
                      label={label}
                      color={mobileColor || color}
                      type={mobileVariant || variant}
                      href={href}
                      target={target}
                      onClick={onClick}
                    />
                  </>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
